import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Checkbox, Form, Input, InputNumber, Modal, Select, Typography } from 'antd';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { crmApi } from '@/core/api/crmApi';
import { leadsApi } from '@/core/api/leadsApi';
import type { Account, Contact } from '@/core/types/crm';
import type { ConvertLeadRequest, Lead } from '@/core/types/lead';
import { getErrorMessage } from '@/core/utils/errors';
import { getLeadDisplayName } from '@/core/utils/format';
import { leadQueryKeys } from '../hooks/useLeads';

export type LifecycleMode = 'qualify' | 'disqualify' | 'convert';
type EntityMode = 'create' | 'link' | 'skip';
interface Props { mode: LifecycleMode | null; lead: Lead; onClose: () => void }
interface Values {
  reason?: string;
  contactMode: EntityMode;
  contactId?: string;
  accountMode: EntityMode;
  accountId?: string;
  createOpportunity: boolean;
  opportunityName?: string;
  amount?: number | null;
  currency?: string;
}

const titles: Record<LifecycleMode, string> = { qualify: 'تأیید سرنخ', disqualify: 'رد سرنخ', convert: 'تبدیل کامل سرنخ' };
const entityModes = (createLabel: string, linkLabel: string, canCreate = true) => [
  { value: 'create', label: createLabel, disabled: !canCreate },
  { value: 'link', label: linkLabel },
  { value: 'skip', label: 'فعلاً ایجاد نشود' },
];
const normalizePhone = (value?: string | null): string => (value || '').replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit))).replace(/\D/g, '');
const normalizeEmail = (value?: string | null): string => (value || '').trim().toLowerCase();
const contactName = (contact: Contact): string => [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.contactPoints[0]?.value || 'مخاطب بدون نام';
const uniqueById = <T extends { id: string }>(groups: Array<readonly T[] | undefined>): T[] => Array.from(new Map(groups.flatMap((items) => items || []).map((item) => [item.id, item])).values());

const useDebouncedValue = (value: string, delay = 300): string => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [delay, value]);
  return debounced;
};

export const LeadLifecycleModal = ({ mode, lead, onClose }: Props): React.ReactElement => {
  const { message } = App.useApp();
  const client = useQueryClient();
  const [form] = Form.useForm<Values>();
  const initializedFor = useRef<string | null>(null);
  const [contactSearch, setContactSearch] = useState('');
  const [accountSearch, setAccountSearch] = useState('');
  const debouncedContactSearch = useDebouncedValue(contactSearch.trim());
  const debouncedAccountSearch = useDebouncedValue(accountSearch.trim());
  const contactMatchTerms = useMemo(() => Array.from(new Set([
    lead.mobile,
    lead.chiefExecutivePhone,
    lead.contactPersonPhone,
    lead.email,
  ].map((value) => value?.trim()).filter((value): value is string => Boolean(value)))), [lead.chiefExecutivePhone, lead.contactPersonPhone, lead.email, lead.mobile]);
  const contactMatchQueries = useQueries({
    queries: contactMatchTerms.map((term) => ({
      queryKey: ['contacts', 'conversion-match', lead.id, term],
      queryFn: () => crmApi.contacts({ page: 1, pageSize: 20, search: term }),
      enabled: mode === 'convert',
      staleTime: 30_000,
    })),
  });
  const contactOptionsQuery = useQuery({
    queryKey: ['contacts', 'conversion-options', lead.id, debouncedContactSearch],
    queryFn: () => crmApi.contacts({ page: 1, pageSize: 100, search: debouncedContactSearch || undefined }),
    enabled: mode === 'convert',
    staleTime: 15_000,
  });
  const accountMatchQuery = useQuery({
    queryKey: ['accounts', 'conversion-match', lead.id, lead.companyName],
    queryFn: () => crmApi.accounts({ page: 1, pageSize: 20, search: lead.companyName || '' }),
    enabled: mode === 'convert' && Boolean(lead.companyName?.trim()),
    staleTime: 30_000,
  });
  const accountOptionsQuery = useQuery({
    queryKey: ['accounts', 'conversion-options', lead.id, debouncedAccountSearch],
    queryFn: () => crmApi.accounts({ page: 1, pageSize: 100, search: debouncedAccountSearch || undefined }),
    enabled: mode === 'convert',
    staleTime: 15_000,
  });

  const contactCandidates = useMemo(() => uniqueById<Contact>(contactMatchQueries.map((query) => query.data?.items)), [contactMatchQueries]);
  const contactOptions = useMemo(() => uniqueById<Contact>([contactCandidates, contactOptionsQuery.data?.items]), [contactCandidates, contactOptionsQuery.data?.items]);
  const accountOptions = useMemo(() => uniqueById<Account>([accountMatchQuery.data?.items, accountOptionsQuery.data?.items]), [accountMatchQuery.data?.items, accountOptionsQuery.data?.items]);

  const matchingContact = useMemo(() => {
    const phones = [lead.mobile, lead.chiefExecutivePhone, lead.contactPersonPhone].map(normalizePhone).filter(Boolean);
    const email = normalizeEmail(lead.email);
    return contactCandidates.find((contact) => contact.contactPoints.some((point) => point.type === 'mobile' ? phones.includes(normalizePhone(point.value)) : Boolean(email) && normalizeEmail(point.value) === email));
  }, [contactCandidates, lead.chiefExecutivePhone, lead.contactPersonPhone, lead.email, lead.mobile]);
  const matchingAccount = useMemo(() => {
    const company = lead.companyName?.trim().toLowerCase();
    return company ? accountMatchQuery.data?.items.find((account) => account.name.trim().toLowerCase() === company) : undefined;
  }, [accountMatchQuery.data?.items, lead.companyName]);

  const matchingOptionsLoading = mode === 'convert' && (contactMatchQueries.some((query) => query.isLoading) || accountMatchQuery.isLoading);
  const optionsHaveError = contactMatchQueries.some((query) => query.isError) || contactOptionsQuery.isError || accountMatchQuery.isError || accountOptionsQuery.isError;

  useEffect(() => {
    // When the modal is destroyed its Form is no longer connected.  Avoid
    // touching the detached instance while closed; the next open resets it.
    if (!mode) { initializedFor.current = null; setContactSearch(''); setAccountSearch(''); return; }
    if (mode !== 'convert') { form.resetFields(); initializedFor.current = null; return; }
    if (matchingOptionsLoading || initializedFor.current === lead.id) return;
    form.setFieldsValue({
      contactMode: matchingContact ? 'link' : 'create',
      contactId: matchingContact?.id,
      accountMode: matchingAccount ? 'link' : lead.companyName ? 'create' : 'skip',
      accountId: matchingAccount?.id,
      createOpportunity: Boolean(lead.companyName),
      opportunityName: lead.companyName ? `فرصت فروش ${lead.companyName}` : `فرصت ${getLeadDisplayName(lead)}`,
      amount: 0,
      currency: 'IRR',
    });
    initializedFor.current = lead.id;
  }, [form, lead, matchingAccount, matchingContact, matchingOptionsLoading, mode]);

  const mutation = useMutation({
    mutationFn: async (values: Values) => {
      if (mode === 'qualify') return leadsApi.qualify(lead.id, { reason: values.reason });
      if (mode === 'disqualify') return leadsApi.disqualify(lead.id, { reason: values.reason || '' });
      if (values.contactMode === 'skip' && values.accountMode === 'skip' && !values.createOpportunity) throw new Error('حداقل یک خروجی برای تبدیل انتخاب کنید.');
      const request: ConvertLeadRequest = {
        createContact: values.contactMode === 'create',
        contactId: values.contactMode === 'link' ? values.contactId || null : null,
        createAccount: values.accountMode === 'create',
        accountId: values.accountMode === 'link' ? values.accountId || null : null,
        createOpportunity: values.createOpportunity,
        opportunityName: values.createOpportunity ? values.opportunityName?.trim() || null : null,
        amount: values.createOpportunity ? values.amount ?? 0 : null,
        currency: values.createOpportunity ? values.currency || 'IRR' : null,
      };
      return leadsApi.convert(lead.id, request);
    },
    onSuccess: () => {
      message.success(mode === 'convert' ? 'سرنخ تبدیل شد و خروجی‌ها به همین سرنخ متصل شدند.' : 'وضعیت سرنخ با موفقیت تغییر کرد.');
      onClose();
      void client.invalidateQueries({ queryKey: leadQueryKeys.all });
      void client.invalidateQueries({ queryKey: ['lead-history', lead.id] });
      void client.invalidateQueries({ queryKey: ['dashboard'] });
      void client.invalidateQueries({ queryKey: ['contacts'] });
      void client.invalidateQueries({ queryKey: ['accounts'] });
      void client.invalidateQueries({ queryKey: ['opportunities'] });
    },
    onError: (error) => message.error(getErrorMessage(error)),
  });

  const loadingOptions = matchingOptionsLoading;
  return (
    <Modal open={Boolean(mode)} title={mode ? titles[mode] : ''} onCancel={onClose} onOk={() => form.submit()} confirmLoading={mutation.isPending || loadingOptions} okButtonProps={{ disabled: loadingOptions }} destroyOnHidden width={720} okText={mode === 'convert' ? 'انجام تبدیل' : 'تأیید'} cancelText="انصراف">
      <Form form={form} layout="vertical" onFinish={(values) => mutation.mutate(values)}>
        {mode === 'qualify' ? <><Alert className="mb-4" type="info" showIcon title="پس از تأیید، سرنخ آماده تبدیل به مشتری می‌شود." /><Form.Item name="reason" label="یادداشت تأیید"><Input.TextArea rows={3} placeholder="نتیجه بررسی و نیاز اصلی مشتری" /></Form.Item></> : null}
        {mode === 'disqualify' ? <><Alert className="mb-4" type="warning" showIcon title="علت رد در تاریخچه سرنخ ثبت می‌شود." /><Form.Item name="reason" label="دلیل رد" rules={[{ required: true, message: 'دلیل رد الزامی است.' }]}><Input.TextArea rows={3} /></Form.Item></> : null}
        {mode === 'convert' ? <div className="space-y-4">
          <div className="rounded-2xl border border-brand-100 bg-brand-50 p-4"><Typography.Text type="secondary">سرنخ در حال تبدیل</Typography.Text><Typography.Title level={5} className="!mb-0 !mt-1">{getLeadDisplayName(lead)}</Typography.Title><Typography.Text type="secondary">{lead.companyName || lead.chiefExecutivePhone || lead.mobile || lead.email}</Typography.Text></div>
          {matchingContact || matchingAccount ? <Alert type="success" showIcon title="مخاطب یا سازمان مشابه پیدا شد" description="برای جلوگیری از ثبت اطلاعات تکراری، مورد موجود به‌صورت خودکار برای اتصال انتخاب شده است." /> : null}
          {optionsHaveError ? <Alert type="warning" showIcon title="دریافت گزینه‌های موجود کامل نشد" description="در صورت اطمینان می‌توانید مخاطب یا سازمان جدید بسازید؛ ثبت اطلاعات تکراری در سرور کنترل می‌شود." /> : null}

          <section className="rounded-2xl border border-slate-200 p-4">
            <div className="mb-4"><Typography.Text strong>۱. مخاطب</Typography.Text><br /><Typography.Text type="secondary">اطلاعات مدیرعامل یا شخص رابط به مخاطب تبدیل می‌شود.</Typography.Text></div>
            <Form.Item name="contactMode" label="عملیات مخاطب" rules={[{ required: true }]}><Select options={entityModes('ایجاد مخاطب جدید', 'اتصال به مخاطب موجود')} /></Form.Item>
            <Form.Item noStyle shouldUpdate={(a, b) => a.contactMode !== b.contactMode}>{({ getFieldValue }) => getFieldValue('contactMode') === 'link' ? <Form.Item name="contactId" label="مخاطب موجود" extra="نام، نام خانوادگی، موبایل یا ایمیل را وارد کنید." rules={[{ required: true, message: 'مخاطب موردنظر را انتخاب کنید.' }]}><Select allowClear showSearch filterOption={false} loading={contactOptionsQuery.isFetching} onSearch={setContactSearch} onClear={() => setContactSearch('')} placeholder="جست‌وجو و انتخاب مخاطب" notFoundContent={contactOptionsQuery.isFetching ? 'در حال جست‌وجو…' : 'مخاطبی پیدا نشد'} options={contactOptions.map((contact) => ({ value: contact.id, label: `${contactName(contact)} · ${contact.contactPoints.map((point) => point.value).join('، ') || 'بدون راه ارتباطی'}` }))} /></Form.Item> : null}</Form.Item>
          </section>

          <section className="rounded-2xl border border-slate-200 p-4">
            <div className="mb-4"><Typography.Text strong>۲. سازمان مشتری</Typography.Text><br /><Typography.Text type="secondary">ایجاد سازمان جدید، ظرفیت واقعی لایسنس مشتری را مصرف می‌کند.</Typography.Text></div>
            <Form.Item name="accountMode" label="عملیات سازمان" rules={[{ required: true }]}><Select options={entityModes('ایجاد سازمان جدید', 'اتصال به سازمان موجود', Boolean(lead.companyName))} /></Form.Item>
            <Form.Item noStyle shouldUpdate={(a, b) => a.accountMode !== b.accountMode}>{({ getFieldValue }) => getFieldValue('accountMode') === 'link' ? <Form.Item name="accountId" label="سازمان موجود" extra="نام شرکت یا شناسه سازمانی را جست‌وجو کنید." rules={[{ required: true, message: 'سازمان موردنظر را انتخاب کنید.' }]}><Select allowClear showSearch filterOption={false} loading={accountOptionsQuery.isFetching} onSearch={setAccountSearch} onClear={() => setAccountSearch('')} placeholder="جست‌وجو و انتخاب سازمان" notFoundContent={accountOptionsQuery.isFetching ? 'در حال جست‌وجو…' : 'سازمانی پیدا نشد'} options={accountOptions.map((account) => ({ value: account.id, label: account.organizationIdentifier ? `${account.name} · ${account.organizationIdentifier}` : account.name }))} /></Form.Item> : null}</Form.Item>
          </section>

          <section className="rounded-2xl border border-slate-200 p-4">
            <Form.Item name="createOpportunity" valuePropName="checked" className="!mb-2"><Checkbox><strong>۳. ایجاد فرصت فروش</strong></Checkbox></Form.Item>
            <Form.Item noStyle shouldUpdate={(a, b) => a.createOpportunity !== b.createOpportunity}>{({ getFieldValue }) => getFieldValue('createOpportunity') ? <div className="grid gap-3 sm:grid-cols-2"><Form.Item className="sm:col-span-2" name="opportunityName" label="عنوان فرصت" rules={[{ required: true, message: 'عنوان فرصت الزامی است.' }]}><Input /></Form.Item><Form.Item name="amount" label="مبلغ"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item><Form.Item name="currency" label="ارز" rules={[{ required: true }]}><Select options={[{ value: 'IRR', label: 'ریال' }, { value: 'USD', label: 'دلار' }, { value: 'EUR', label: 'یورو' }]} /></Form.Item></div> : null}</Form.Item>
          </section>
        </div> : null}
      </Form>
    </Modal>
  );
};
